import { createContext, useEffect, useMemo, useState } from 'react';
import { Locale } from 'date-fns';
import { LicenseInfo } from '@mui/x-license';
import AppThemeProvider from './AppThemeProvider';
import AppUserProvider from './AppUserProvider';
import AppAuthProvider from './AppAuthProvider';
import Layout from '../template/components/Layout/Layout';
import AppRouterProvider from './AppRouterProvider';
import getDefaultDateFnsLocale from '../config/date/getDefaultDateFnsLocale';
import getDateFnsLocaleFromString from '../config/date/getDateFnsLocaleFromString';
import { UserTheme } from '../features/users/enums/UserTheme';
import AppSentryProvider from './AppSentryProvider';
import AppMaintenanceProvider from './AppMaintenanceProvider';
import User from '../features/users/models/User';
import { MasterDataProvider } from './MasterDataContext'; // Importation du MasterDataProvider

interface AppContextInterface {
  user: User | null;
  setUser: (user: User | null) => void;
  regionalFormat: Locale;
  setRegionalFormat: (regionalFormatStr: string) => void;
  darkMode: boolean;
  setDarkMode: (enabled: boolean) => void;
}

const defaultDateLocale = getDefaultDateFnsLocale();

LicenseInfo.setLicenseKey(import.meta.env.VITE_MUI_LICENSE_KEY as string);

export const AppContext = createContext<AppContextInterface>({
  user: null,
  setUser: () => {},
  regionalFormat: defaultDateLocale,
  setRegionalFormat: () => {},
  darkMode: false,
  setDarkMode: () => {},
});

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [regionalFormat, setRegionalFormat] = useState<Locale>(defaultDateLocale);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('theme') === UserTheme.Dark);

  const setRegionalFormatFromStr = (regionalFormatStr: string) => {
    setRegionalFormat(getDateFnsLocaleFromString(regionalFormatStr));
  };

  useEffect(() => {
    if (!sessionStorage.getItem('redirectURI')) {
      sessionStorage.setItem('redirectURI', window.location.href);
    }
  }, []);

  useEffect(() => {
    if (user?.regionalFormat) {
      setRegionalFormatFromStr(user?.regionalFormat);
    }
  }, [user?.regionalFormat]);

  useEffect(() => {
    if (user?.theme) {
      setDarkMode(user?.theme === UserTheme.Dark);
      localStorage.setItem('theme', user?.theme === UserTheme.Dark ? UserTheme.Dark : UserTheme.Light);
    }
  }, [user?.theme]);

  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      regionalFormat,
      setRegionalFormat: setRegionalFormatFromStr,
      darkMode,
      setDarkMode,
    }),
    [user, regionalFormat, darkMode],
  );

  return (
    <AppContext.Provider value={contextValue}>
      <AppThemeProvider>
        <AppAuthProvider>
          <AppSentryProvider>
            <AppMaintenanceProvider>
              <AppUserProvider>
                <MasterDataProvider>
                  <AppRouterProvider>
                    <Layout />
                  </AppRouterProvider>
                </MasterDataProvider>
              </AppUserProvider>
            </AppMaintenanceProvider>
          </AppSentryProvider>
        </AppAuthProvider>
      </AppThemeProvider>
    </AppContext.Provider>
  );
}

export default App;
